// ============================================================
// Free Trial Component
// OTP-gated 3-question trial. Email verified but never stored.
// ============================================================

import React, { useState, useEffect } from 'react';
import OtpGate from './OtpGate';
import { getTrialQuestions, submitTrialAnswers, getCurrentPrice } from '../services/api';

function FreeTrial({ onExit }) {
  const [gccStatus, setGccStatus] = useState('aspiring');
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [priceInfo, setPriceInfo] = useState(null);

  useEffect(() => {
    if (verifiedEmail) {
      getTrialQuestions().then(data => setQuestions(data.questions));
    }
  }, [verifiedEmail]);

  // Fetches the live price/slots-remaining so the results screen can
  // repeat the launch offer right at the moment someone's deciding
  // whether to convert to paid — this was previously missing here
  // entirely (only shown on Landing.js), even though repeating the
  // offer right before the decision point is exactly when it matters.
  // Uses the same live getCurrentPrice() call Intake.js already uses,
  // not hardcoded text, so this stays accurate if the launch window
  // closes and price reverts to the standard rate.
  useEffect(() => {
    getCurrentPrice().then(setPriceInfo).catch(() => {});
  }, []);

  function selectAnswer(questionId, key) {
    setAnswers({ ...answers, [questionId]: key });
  }

  async function handleNext() {
    if (currentIdx < questions.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      setLoading(true);
      const data = await submitTrialAnswers(verifiedEmail, answers);
      setResults(data.results);
      setLoading(false);
    }
  }

  if (!verifiedEmail) {
    return (
      <div className="trial-screen">
        <h2>Try 3 Free Questions</h2>
        <p className="sub">See how CHART Rubric works. Takes 2 minutes. No payment required.</p>
        <p className="trial-preview-note">These 3 sample questions use general insights. Your full paid report is tailored specifically to your career stage and industry.</p>

        <div className="experience-note">
          <p>CHART Rubric works best for those with 4+ years of experience. If you're earlier in your career, this alternative might suit you better.</p>
          <a href="https://www.sarthiedge.com/sarthi-professionals" target="_blank" rel="noopener noreferrer">
            <button type="button" className="btn-secondary experience-redirect-btn">Explore Sarthi Professionals</button>
          </a>
        </div>

        <label className="field">Where are you right now?</label>
        <div className="radio-group">
          <label className="radio-option">
            <input
              type="radio"
              name="trialGccStatus"
              checked={gccStatus === 'aspiring'}
              onChange={() => setGccStatus('aspiring')}
            />
            I'm aspiring to get into a GCC
          </label>
          <label className="radio-option">
            <input
              type="radio"
              name="trialGccStatus"
              checked={gccStatus === 'within_gcc'}
              onChange={() => setGccStatus('within_gcc')}
            />
            I'm within a GCC, but trying to move to a different or better role, or currently facing some hurdles and want to understand where I stand
          </label>
        </div>

        <OtpGate purpose="trial" onVerified={setVerifiedEmail} />
        <button className="link-button" onClick={onExit}>← Back to home</button>
      </div>
    );
  }

  if (results) {
    return (
      <div className="trial-screen">
        <h2>Your Trial Results</h2>
        <p className="sub">This is a taste of the insight style. The full report covers all 5 CHART dimensions with 20 questions, tailored to your career stage and industry.</p>
        {results.map(r => (
          <div key={r.id} className="trial-result-card">
            <div className="trial-result-category">{r.category}</div>
            <p>{r.insightText}</p>
            {r.timeline && (
              <div className="trial-mini-timeline">
                <div className="trial-timeline-item"><span className="trial-timeline-label">This week</span><p>{r.timeline.thisWeek}</p></div>
                <div className="trial-timeline-item"><span className="trial-timeline-label">This month</span><p>{r.timeline.thisMonth}</p></div>
              </div>
            )}
          </div>
        ))}

        {/* Fuller sample section - shows real report depth, illustrative persona */}
        <div className="sample-full-section">
          <div className="sample-full-label">A closer look at your full report (illustrative example)</div>
          <div className="sample-persona">Sample: Manager, IT/Technology — Adaptability score 2 / 4</div>
          <div className="sample-tier-tag">Worth deliberate practice</div>
          <p className="sample-rationale">You're capable of adapting, but tend to wait for full clarity before moving. In fast-changing environments, waiting too long often costs more than acting on partial information.</p>
          <div className="sample-action">
            <span className="sample-action-timeline">Build this over the next 2–4 weeks</span>
            <p>Over the next 2–4 weeks, practice acting on partial information at least once, rather than waiting for certainty.</p>
          </div>
          <div className="sample-action">
            <span className="sample-action-timeline">Build this over the next 2–4 weeks</span>
            <p>When a new tool or process is introduced, spend focused time learning it early, rather than waiting until it's required.</p>
          </div>
          <div className="sample-path-preview">
            <strong>First 30 days:</strong> Spend the next 30 days working specifically on Adaptability. Pick one or two of the actions above and practice them in real situations at work.
          </div>
          <p className="sample-footnote">Your real report covers all 5 dimensions this way — plus a full 30/60/90/180-day path, a summary of your top priorities, and a personal note.</p>
        </div>

        {priceInfo && (
          <div className="pricing-banner">
            <strong>₹{priceInfo.amount}</strong>{' '}
            {priceInfo.priceType === 'launch' && (
              <span className="launch-note">Launch price — {priceInfo.remaining} slots left</span>
            )}
          </div>
        )}
        <button className="btn-primary" onClick={onExit}>Get Your Full CHART Rubric Report →</button>
      </div>
    );
  }

  if (!questions.length) return <div className="trial-screen">Loading questions...</div>;

  const q = questions[currentIdx];
  const selected = answers[q.id];

  return (
    <div className="trial-screen">
      <div className="trial-progress">Question {currentIdx + 1} of {questions.length}</div>
      <p className="trial-category">{q.category}</p>
      <p className="question-text">{q.text}</p>
      <div className="options">
        {q.options.map(opt => (
          <label key={opt.key} className={selected === opt.key ? 'selected' : ''}>
            <input
              type="radio"
              checked={selected === opt.key}
              onChange={() => selectAnswer(q.id, opt.key)}
            />
            {opt.label}
          </label>
        ))}
      </div>
      <button className="btn-primary" onClick={handleNext} disabled={!selected || loading}>
        {loading ? 'Loading...' : currentIdx === questions.length - 1 ? 'See My Results' : 'Next →'}
      </button>
    </div>
  );
}

export default FreeTrial;
